import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Facebook, Youtube } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer style={{ backgroundColor: '#000', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 mb-16">

          {/* Brand */}
          <div className="md:col-span-1">
            <img
              src="/tc_logo.png"
              alt="Total Composites"
              className="w-auto object-contain mb-5"
              style={{ filter: 'invert(1)', height: 52 }}
            />
            <p className="text-xs leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('footer.tagline')}
            </p>
            {/* Social */}
            <div className="flex items-center gap-4">
              {[
                { Icon: Instagram, url: 'https://www.instagram.com/totalcomposites/', label: 'Instagram' },
                { Icon: Facebook, url: 'https://www.facebook.com/p/Total-Composites-Technologies-Ltd-61569109453350/', label: 'Facebook' },
                { Icon: Youtube, url: 'https://www.youtube.com/channel/UCM3icObRUWefp_Csg889UUQ', label: 'YouTube' },
              ].map(({ Icon, url, label }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#E8520A')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
                  className="transition-colors"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest mb-5" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('footer.productsTitle')}</h4>
            <ul className="space-y-3">
              {[
                { label: t('nav.truckCampers'), href: '/products?tab=pre-assembled', badge: t('nav.new2026') },
                { label: t('nav.keiTruck'), href: '/products?tab=kei-truck', badge: t('nav.new2026') },
                { label: t('nav.flatKits'), href: '/products?tab=expedition-kits' },
                { label: t('nav.truckBodies'), href: '/products?tab=truck-bodies' },
                { label: t('nav.customProjects'), href: '/products?tab=custom' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-xs transition-colors flex items-center gap-2"
                    style={{ color: 'rgba(255,255,255,0.45)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
                  >
                    {link.label}
                    {link.badge && (
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#E8520A', color: '#fff', fontSize: '0.55rem' }}>
                        {link.badge}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest mb-5" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('footer2.resourcesTitle')}</h4>
            <ul className="space-y-3">
              {[
                { label: t('nav.builderResources'), href: '/builder-resources' },
                { label: t('nav.partners'), href: '/partners' },
                { label: t('nav.gallery'), href: '/gallery' },
                { label: t('nav.articles'), href: '/articles' },
                { label: t('nav.media'), href: '/media' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-xs transition-colors"
                    style={{ color: 'rgba(255,255,255,0.45)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest mb-5" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('footer.companyTitle')}</h4>
            <ul className="space-y-3">
              {[
                { label: t('footer.aboutUs'), href: '/about' },
                { label: t('nav.whyTC'), href: '/why-total-composites' },
                { label: 'FAQ', href: '/why-total-composites#faq' },
                { label: t('nav.expeditions'), href: '/expedition' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-xs transition-colors"
                    style={{ color: 'rgba(255,255,255,0.45)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest mb-5" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('footer.contactTitle')}</h4>
            <div className="space-y-4">
              <a
                href="https://maps.app.goo.gl/R83NYMrWMjo5BXMi7"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
              >
                <MapPin size={14} style={{ color: '#E8520A', flexShrink: 0 }} />
                <span className="text-xs transition-colors" style={{ color: 'rgba(255,255,255,0.45)' }}
                  onMouseEnter={(e) => (e.target.style.color = '#fff')}
                  onMouseLeave={(e) => (e.target.style.color = 'rgba(255,255,255,0.45)')}
                >Victoria, BC, Canada</span>
              </a>
              <a href="tel:2504770375" className="flex items-center gap-3">
                <Phone size={14} style={{ color: '#E8520A', flexShrink: 0 }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>250 477 0375</span>
              </a>
              <a href="mailto:info@totalcomposites.com" className="flex items-center gap-3">
                <Mail size={14} style={{ color: '#E8520A', flexShrink: 0 }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>info@totalcomposites.com</span>
              </a>

              {/* Online Shop Button */}
              <div className="pt-3">
                <a
                  href="https://www.expeditionupfitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg transition-all duration-200 hover:opacity-90"
                  style={{ 
                    backgroundColor: '#E8520A',
                    color: '#fff'
                  }}
                >
                  {t('footer.onlineShop')}
                </a>
              </div>

            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
            &copy; {new Date().getFullYear()} {t('footer.copyright')}
          </p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
            {t('footer.engineered')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
